import { z } from 'zod'
import { ContractSourceMetaSchema } from './questionnaire'

export const CustomerApplicationSourceTypeSchema = z.enum(['questionnaire_record', 'manual'])
export type CustomerApplicationSourceType = z.infer<typeof CustomerApplicationSourceTypeSchema>

export const CustomerApplicationFieldSourceStateSchema = z.enum([
  'prefilled_from_question',
  'manual',
  'overridden',
  'missing_source',
])
export type CustomerApplicationFieldSourceState = z.infer<
  typeof CustomerApplicationFieldSourceStateSchema
>

export const QuestionnaireSourceRecordLinkStatusSchema = z.enum([
  'available',
  'linked',
  'unavailable',
])
export type QuestionnaireSourceRecordLinkStatus = z.infer<
  typeof QuestionnaireSourceRecordLinkStatusSchema
>

export const QuestionnaireSourceRecordArchiveStatusSchema = z.enum(['active', 'archived'])
export type QuestionnaireSourceRecordArchiveStatus = z.infer<
  typeof QuestionnaireSourceRecordArchiveStatusSchema
>
export const QuestionnaireSourceRecordArchiveStatusFilterSchema = z.union([
  z.literal('all'),
  QuestionnaireSourceRecordArchiveStatusSchema,
])
export type QuestionnaireSourceRecordArchiveStatusFilter = z.infer<
  typeof QuestionnaireSourceRecordArchiveStatusFilterSchema
>

export const CustomerFieldMappingGroupSchema = z.enum([
  'workflow',
  'company',
  'contact',
  'payment',
  'address',
  'receiving',
  'sales',
  'classification',
  'region',
  'notes',
  'credit',
  'legacy',
])
export type CustomerFieldMappingGroup = z.infer<typeof CustomerFieldMappingGroupSchema>

export const CustomerFieldMappingTargetSchema = z.enum([
  'formType',
  'changeType',
  'existingCustomerCode',
  'customerSource',
  'groupCustomerId',
  'customerShortName',
  'companyFullName',
  'companyName',
  'ownerName',
  'taxId',
  'employeeCountRange',
  'deliveryAddress',
  'invoiceAddress',
  'registeredAddress',
  'billingEmail',
  'deliveryTimeNote',
  'businessHours',
  'department',
  'salesRepSpecial',
  'salesRepDiv1',
  'salesRepDiv2',
  'salesRepDiv3',
  'salesRepSpecialCh',
  'salesRepDiv4',
  'customerCategory',
  'customerChannel',
  'customerType',
  'region',
  'districtTaipei',
  'districtNewTaipei',
  'districtTaichung',
  'districtTainan',
  'districtKaohsiung',
  'remark',
  'submittedBy',
  'foodBusinessRegNo',
  'phone1',
  'phone2',
  'fax',
  'email',
  'industry',
  'companySize',
  'website',
  'companyAddress',
  'companyPhone',
  'contactName',
  'contactTitle',
  'contactEmail',
  'contactPhone',
  'decisionRole',
  'needSummary',
  'expectedValue',
  'budgetRange',
  'paymentTerms',
  'creditLimit',
  'ownerEmail',
  'salesRegion',
  'internalNotes',
])
export type CustomerFieldMappingTarget = z.infer<typeof CustomerFieldMappingTargetSchema>

export const CustomerFieldBindingTransformSchema = z.enum([
  'none',
  'trim',
  'number',
  'currency',
  'phone',
  'email',
  'url',
  'date',
  'join_choices',
])
export type CustomerFieldBindingTransform = z.infer<typeof CustomerFieldBindingTransformSchema>

export const CustomerFieldBindingOverwritePolicySchema = z.enum([
  'prefill_if_empty',
  'overwrite_manual',
  'ask_before_overwrite',
])
export type CustomerFieldBindingOverwritePolicy = z.infer<
  typeof CustomerFieldBindingOverwritePolicySchema
>

export const CustomerFieldBindingConfidenceHintSchema = z.enum(['exact', 'likely', 'manual_review'])
export type CustomerFieldBindingConfidenceHint = z.infer<
  typeof CustomerFieldBindingConfidenceHintSchema
>

export const customerFieldTargetOptions: Array<{
  value: CustomerFieldMappingTarget
  label: string
  shortLabel: string
  group: CustomerFieldMappingGroup
  dbFieldName?: string
  dataType?: string
}> = [
  {
    value: 'formType',
    label: '流程控制 / 新客戶建檔選擇',
    shortLabel: '建檔選擇',
    group: 'workflow',
    dbFieldName: 'form_type',
    dataType: 'ENUM',
  },
  {
    value: 'changeType',
    label: '流程控制 / 資料變更選擇',
    shortLabel: '變更類型',
    group: 'workflow',
    dbFieldName: 'change_type',
    dataType: 'ENUM',
  },
  {
    value: 'existingCustomerCode',
    label: '流程控制 / 既有客戶代號',
    shortLabel: '既有客戶代號',
    group: 'workflow',
    dbFieldName: 'existing_customer_code',
    dataType: 'VARCHAR(50)',
  },
  {
    value: 'customerSource',
    label: '客戶來源與基本資料 / 新客戶來源',
    shortLabel: '客戶來源',
    group: 'company',
    dbFieldName: 'customer_source',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'groupCustomerId',
    label: '客戶來源與基本資料 / 集團客戶代號或全名',
    shortLabel: '集團客戶',
    group: 'company',
    dbFieldName: 'group_customer_id',
    dataType: 'VARCHAR(50)',
  },
  {
    value: 'customerShortName',
    label: '客戶來源與基本資料 / 客戶簡稱',
    shortLabel: '客戶簡稱',
    group: 'company',
    dbFieldName: 'customer_short_name',
    dataType: 'NVARCHAR(100)',
  },
  {
    value: 'companyFullName',
    label: '客戶來源與基本資料 / 統編抬頭（公司全名）',
    shortLabel: '公司全名',
    group: 'company',
    dbFieldName: 'company_full_name',
    dataType: 'NVARCHAR(200)',
  },
  {
    value: 'ownerName',
    label: '客戶來源與基本資料 / 負責人',
    shortLabel: '負責人',
    group: 'company',
    dbFieldName: 'owner_name',
    dataType: 'NVARCHAR(50)',
  },
  {
    value: 'contactName',
    label: '聯絡資訊 / 聯絡人',
    shortLabel: '聯絡人',
    group: 'contact',
    dbFieldName: 'contact_name',
    dataType: 'NVARCHAR(50)',
  },
  {
    value: 'phone1',
    label: '聯絡資訊 / 市話',
    shortLabel: '市話',
    group: 'contact',
    dbFieldName: 'phone_1',
    dataType: 'VARCHAR(30)',
  },
  {
    value: 'phone2',
    label: '聯絡資訊 / 手機',
    shortLabel: '手機',
    group: 'contact',
    dbFieldName: 'phone_2',
    dataType: 'VARCHAR(30)',
  },
  {
    value: 'fax',
    label: '聯絡資訊 / 傳真號碼',
    shortLabel: '傳真',
    group: 'contact',
    dbFieldName: 'fax',
    dataType: 'VARCHAR(30)',
  },
  {
    value: 'email',
    label: '聯絡資訊 / E-Mail',
    shortLabel: 'Email',
    group: 'contact',
    dbFieldName: 'email',
    dataType: 'VARCHAR(100)',
  },
  {
    value: 'taxId',
    label: '聯絡資訊 / 統一編號',
    shortLabel: '統編',
    group: 'company',
    dbFieldName: 'tax_id',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'employeeCountRange',
    label: '付款與規模 / 員工人數',
    shortLabel: '員工人數',
    group: 'payment',
    dbFieldName: 'employee_count_range',
    dataType: 'ENUM',
  },
  {
    value: 'deliveryAddress',
    label: '地址資訊 / 送貨地址',
    shortLabel: '送貨地址',
    group: 'address',
    dbFieldName: 'delivery_address',
    dataType: 'NVARCHAR(300)',
  },
  {
    value: 'invoiceAddress',
    label: '地址資訊 / 發票地址',
    shortLabel: '發票地址',
    group: 'address',
    dbFieldName: 'invoice_address',
    dataType: 'NVARCHAR(300)',
  },
  {
    value: 'registeredAddress',
    label: '地址資訊 / 登記地址',
    shortLabel: '登記地址',
    group: 'address',
    dbFieldName: 'registered_address',
    dataType: 'NVARCHAR(300)',
  },
  {
    value: 'billingEmail',
    label: '帳務與收貨 / 帳單收件Mail',
    shortLabel: '帳單 Mail',
    group: 'receiving',
    dbFieldName: 'billing_email',
    dataType: 'VARCHAR(100)',
  },
  {
    value: 'deliveryTimeNote',
    label: '帳務與收貨 / 可收貨時間',
    shortLabel: '可收貨時間',
    group: 'receiving',
    dbFieldName: 'delivery_time_note',
    dataType: 'NVARCHAR(200)',
  },
  {
    value: 'businessHours',
    label: '帳務與收貨 / 營業時段、公休日',
    shortLabel: '營業時段',
    group: 'receiving',
    dbFieldName: 'business_hours',
    dataType: 'NVARCHAR(200)',
  },
  {
    value: 'department',
    label: '部門與業務歸屬 / 部門別',
    shortLabel: '部門別',
    group: 'sales',
    dbFieldName: 'department',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'salesRepSpecial',
    label: '部門與業務歸屬 / 特販業務',
    shortLabel: '特販業務',
    group: 'sales',
    dbFieldName: 'sales_rep_special',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'salesRepDiv1',
    label: '部門與業務歸屬 / 營一業務',
    shortLabel: '營一業務',
    group: 'sales',
    dbFieldName: 'sales_rep_div1',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'salesRepDiv2',
    label: '部門與業務歸屬 / 營二業務',
    shortLabel: '營二業務',
    group: 'sales',
    dbFieldName: 'sales_rep_div2',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'salesRepDiv3',
    label: '部門與業務歸屬 / 營三業務',
    shortLabel: '營三業務',
    group: 'sales',
    dbFieldName: 'sales_rep_div3',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'salesRepSpecialCh',
    label: '部門與業務歸屬 / 特通業務',
    shortLabel: '特通業務',
    group: 'sales',
    dbFieldName: 'sales_rep_special_ch',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'salesRepDiv4',
    label: '部門與業務歸屬 / 營四業務',
    shortLabel: '營四業務',
    group: 'sales',
    dbFieldName: 'sales_rep_div4',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'customerCategory',
    label: '客戶分類 / 客戶類別',
    shortLabel: '客戶類別',
    group: 'classification',
    dbFieldName: 'customer_category',
    dataType: 'ENUM',
  },
  {
    value: 'customerChannel',
    label: '客戶分類 / 客戶通路別',
    shortLabel: '通路別',
    group: 'classification',
    dbFieldName: 'customer_channel',
    dataType: 'VARCHAR(20)',
  },
  {
    value: 'customerType',
    label: '客戶分類 / 型態別',
    shortLabel: '型態別',
    group: 'classification',
    dbFieldName: 'customer_type',
    dataType: 'VARCHAR(30)',
  },
  {
    value: 'region',
    label: '區域資訊 / 客戶區域別',
    shortLabel: '區域別',
    group: 'region',
    dbFieldName: 'region',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'districtTaipei',
    label: '區域資訊 / 臺北市哪一區',
    shortLabel: '臺北市區',
    group: 'region',
    dbFieldName: 'district_taipei',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'districtNewTaipei',
    label: '區域資訊 / 新北市哪一區',
    shortLabel: '新北市區',
    group: 'region',
    dbFieldName: 'district_newtaipei',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'districtTaichung',
    label: '區域資訊 / 臺中市哪一區',
    shortLabel: '臺中市區',
    group: 'region',
    dbFieldName: 'district_taichung',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'districtTainan',
    label: '區域資訊 / 臺南市哪一區',
    shortLabel: '臺南市區',
    group: 'region',
    dbFieldName: 'district_tainan',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'districtKaohsiung',
    label: '區域資訊 / 高雄市哪一區',
    shortLabel: '高雄市區',
    group: 'region',
    dbFieldName: 'district_kaohsiung',
    dataType: 'VARCHAR(10)',
  },
  {
    value: 'remark',
    label: '其他欄位 / 備註',
    shortLabel: '備註',
    group: 'notes',
    dbFieldName: 'remark',
    dataType: 'NVARCHAR(500)',
  },
  {
    value: 'submittedBy',
    label: '其他欄位 / 填單人員',
    shortLabel: '填單人員',
    group: 'notes',
    dbFieldName: 'submitted_by',
    dataType: 'NVARCHAR(50)',
  },
  {
    value: 'foodBusinessRegNo',
    label: '其他欄位 / 食品業者登入字號',
    shortLabel: '食品登入字號',
    group: 'notes',
    dbFieldName: 'food_business_reg_no',
    dataType: 'VARCHAR(50)',
  },
  {
    value: 'companyName',
    label: '相容欄位 / 公司名稱',
    shortLabel: '公司名稱',
    group: 'legacy',
    dbFieldName: 'company_name',
    dataType: 'TEXT',
  },
  {
    value: 'industry',
    label: '相容欄位 / 產業',
    shortLabel: '產業',
    group: 'legacy',
    dbFieldName: 'industry',
    dataType: 'TEXT',
  },
  {
    value: 'companySize',
    label: '相容欄位 / 公司規模',
    shortLabel: '公司規模',
    group: 'legacy',
    dbFieldName: 'company_size',
    dataType: 'TEXT',
  },
  {
    value: 'website',
    label: '相容欄位 / 網站',
    shortLabel: '網站',
    group: 'legacy',
    dbFieldName: 'website',
    dataType: 'TEXT',
  },
  {
    value: 'companyAddress',
    label: '相容欄位 / 地址',
    shortLabel: '地址',
    group: 'legacy',
    dbFieldName: 'company_address',
    dataType: 'TEXT',
  },
  {
    value: 'companyPhone',
    label: '相容欄位 / 公司電話',
    shortLabel: '公司電話',
    group: 'legacy',
    dbFieldName: 'company_phone',
    dataType: 'TEXT',
  },
  {
    value: 'contactTitle',
    label: '相容欄位 / 職稱',
    shortLabel: '職稱',
    group: 'legacy',
    dbFieldName: 'contact_title',
    dataType: 'TEXT',
  },
  {
    value: 'contactEmail',
    label: '相容欄位 / 聯絡 Email',
    shortLabel: '聯絡 Email',
    group: 'legacy',
    dbFieldName: 'contact_email',
    dataType: 'VARCHAR(320)',
  },
  {
    value: 'contactPhone',
    label: '相容欄位 / 聯絡電話',
    shortLabel: '聯絡電話',
    group: 'legacy',
    dbFieldName: 'contact_phone',
    dataType: 'TEXT',
  },
  {
    value: 'decisionRole',
    label: '相容欄位 / 決策角色',
    shortLabel: '決策角色',
    group: 'legacy',
    dbFieldName: 'decision_role',
    dataType: 'TEXT',
  },
  {
    value: 'needSummary',
    label: '相容欄位 / 需求摘要',
    shortLabel: '需求摘要',
    group: 'legacy',
    dbFieldName: 'need_summary',
    dataType: 'TEXT',
  },
  {
    value: 'expectedValue',
    label: '相容欄位 / 預估商機金額',
    shortLabel: '預估金額',
    group: 'legacy',
    dbFieldName: 'expected_value',
    dataType: 'INTEGER',
  },
  {
    value: 'budgetRange',
    label: '相容欄位 / 預算範圍',
    shortLabel: '預算範圍',
    group: 'legacy',
  },
  {
    value: 'paymentTerms',
    label: '相容欄位 / 付款條件',
    shortLabel: '付款條件',
    group: 'legacy',
    dbFieldName: 'payment_terms',
    dataType: 'TEXT',
  },
  {
    value: 'creditLimit',
    label: '相容欄位 / 建議授信額度',
    shortLabel: '授信額度',
    group: 'legacy',
    dbFieldName: 'credit_limit',
    dataType: 'INTEGER',
  },
  {
    value: 'ownerEmail',
    label: '相容欄位 / 負責業務 Email',
    shortLabel: '業務 Email',
    group: 'legacy',
    dbFieldName: 'owner_email',
    dataType: 'VARCHAR(320)',
  },
  {
    value: 'salesRegion',
    label: '相容欄位 / 業務區域',
    shortLabel: '業務區域',
    group: 'legacy',
    dbFieldName: 'sales_region',
    dataType: 'TEXT',
  },
  {
    value: 'internalNotes',
    label: '相容欄位 / 內部備註',
    shortLabel: '內部備註',
    group: 'legacy',
    dbFieldName: 'internal_notes',
    dataType: 'TEXT',
  },
]

export const QuestionnaireSourceAnswerPreviewSchema = z.object({
  questionId: z.string(),
  questionTitle: z.string(),
  valuePreview: z.string(),
})
export type QuestionnaireSourceAnswerPreview = z.infer<
  typeof QuestionnaireSourceAnswerPreviewSchema
>

export const QuestionnaireSourceAnswerDetailSchema = z.object({
  questionId: z.string(),
  questionTitle: z.string(),
  questionType: z.string(),
  valuePreview: z.string(),
  mappedTarget: CustomerFieldMappingTargetSchema.optional(),
  mappedTargetLabel: z.string().optional(),
  mappedTargetFullLabel: z.string().optional(),
  mappedDbFieldName: z.string().optional(),
  mappedDataType: z.string().optional(),
  sourceState: CustomerApplicationFieldSourceStateSchema,
})
export type QuestionnaireSourceAnswerDetail = z.infer<typeof QuestionnaireSourceAnswerDetailSchema>

export const QuestionnaireSourceRecordContractSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  formId: z.string(),
  formTitle: z.string(),
  responseId: z.string(),
  submittedAt: z.string(),
  submittedByName: z.string().optional(),
  submittedByEmail: z.string().optional(),
  salesUserId: z.string().optional(),
  salesUserName: z.string().optional(),
  firstAnswers: z.array(QuestionnaireSourceAnswerPreviewSchema),
  answerDetails: z.array(QuestionnaireSourceAnswerDetailSchema),
  linkStatus: QuestionnaireSourceRecordLinkStatusSchema,
  archiveStatus: QuestionnaireSourceRecordArchiveStatusSchema.optional(),
  archivedAt: z.string().optional(),
  linkedApplicationId: z.string().optional(),
  linkedApplicationNumber: z.string().optional(),
  linkedApplicationStatus: z.string().optional(),
  sourceMeta: ContractSourceMetaSchema,
})
export type QuestionnaireSourceRecordContract = z.infer<
  typeof QuestionnaireSourceRecordContractSchema
>

export const CustomerFieldBindingContractSchema = z.object({
  id: z.string(),
  formId: z.string(),
  questionId: z.string(),
  questionTitle: z.string(),
  targetField: CustomerFieldMappingTargetSchema,
  targetLabel: z.string(),
  targetFullLabel: z.string().optional(),
  dbFieldName: z.string().optional(),
  dataType: z.string().optional(),
  sourceType: z.literal('question_answer'),
  transform: CustomerFieldBindingTransformSchema,
  requiredForApproval: z.boolean(),
  overwritePolicy: CustomerFieldBindingOverwritePolicySchema,
  confidenceHint: CustomerFieldBindingConfidenceHintSchema,
  updatedAt: z.string(),
})
export type CustomerFieldBindingContract = z.infer<typeof CustomerFieldBindingContractSchema>

export const CustomerApplicationFieldValueContractSchema = z.object({
  fieldKey: CustomerFieldMappingTargetSchema,
  label: z.string(),
  targetLabel: z.string().optional(),
  dbFieldName: z.string().optional(),
  dataType: z.string().optional(),
  value: z.string(),
  originalValue: z.string().optional(),
  sourceState: CustomerApplicationFieldSourceStateSchema,
  sourceQuestionId: z.string().optional(),
  sourceQuestionTitle: z.string().optional(),
  sourceAnswerId: z.string().optional(),
})
export type CustomerApplicationFieldValueContract = z.infer<
  typeof CustomerApplicationFieldValueContractSchema
>

export const CustomerApplicationSourceSummaryContractSchema = z.object({
  sourceType: CustomerApplicationSourceTypeSchema,
  sourceRecordId: z.string().optional(),
  responseId: z.string().optional(),
  formId: z.string().optional(),
  formTitle: z.string().optional(),
  submittedAt: z.string().optional(),
  createdByName: z.string().optional(),
  linkStatus: QuestionnaireSourceRecordLinkStatusSchema.optional(),
  archiveStatus: QuestionnaireSourceRecordArchiveStatusSchema.optional(),
  archivedAt: z.string().optional(),
})
export type CustomerApplicationSourceSummaryContract = z.infer<
  typeof CustomerApplicationSourceSummaryContractSchema
>

export const CustomerApplicationSourceLinkStatusSchema = z.enum(['active', 'archived'])
export type CustomerApplicationSourceLinkStatus = z.infer<
  typeof CustomerApplicationSourceLinkStatusSchema
>

export const CustomerApplicationSourceLinkContractSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  applicationId: z.string(),
  sourceRecordId: z.string(),
  responseId: z.string(),
  formId: z.string(),
  sourceType: z.literal('questionnaire_record'),
  status: CustomerApplicationSourceLinkStatusSchema,
  createdAt: z.string(),
  createdByEmail: z.string(),
  sourceMeta: ContractSourceMetaSchema,
})
export type CustomerApplicationSourceLinkContract = z.infer<
  typeof CustomerApplicationSourceLinkContractSchema
>

export const ListQuestionnaireSourceRecordsParamsSchema = z.object({
  formId: z.string().optional(),
  keyword: z.string().optional(),
  salesUserId: z.string().optional(),
  linkStatus: z.union([z.literal('all'), QuestionnaireSourceRecordLinkStatusSchema]).default('all'),
  archiveStatus: QuestionnaireSourceRecordArchiveStatusFilterSchema.default('active'),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
})
export type ListQuestionnaireSourceRecordsParams = z.input<
  typeof ListQuestionnaireSourceRecordsParamsSchema
>

export type ListQuestionnaireSourceRecordsResult = {
  data: QuestionnaireSourceRecordContract[]
  meta: z.infer<typeof ContractSourceMetaSchema> & {
    total: number
    page: number
    limit: number
    archive_status_filter?: QuestionnaireSourceRecordArchiveStatusFilter
  }
}

export type CustomerApplicationSourcePreviewContract = {
  record: QuestionnaireSourceRecordContract
  fieldValues: CustomerApplicationFieldValueContract[]
  sourceMeta: z.infer<typeof ContractSourceMetaSchema>
}
